import { BaseService } from './base';

// ============================================
// Types
// ============================================

export interface AmazonProductType {
  id: string;
  label: string;
}

export interface EnrichedField {
  value: string | string[];
  source: 'seller' | 'vision' | 'llm' | 'rule' | 'barcode';
  confidence: number;
}

export interface ValidationIssue {
  field: string;
  severity: 'error' | 'warning' | 'info';
  rule: string;
  message: string;
  suggested_values?: string[];
}

export interface ValidationResult {
  valid: boolean;
  completeness: {
    overall?: number;
    filled?: number;
    total?: number;
    percentage?: number;
    required?: {
      filled: number;
      total: number;
    };
  };
  issues: ValidationIssue[];
  summary: {
    errors?: number;
    warnings?: number;
    info?: number;
    error_count?: number;
    warning_count?: number;
    info_count?: number;
  };
}

export interface EnrichmentResult {
  id: string;
  upload_product_id: string;
  amazon_product_type: string;
  enriched_fields: Record<string, EnrichedField>;
  validation_input: any;
  validation_result: ValidationResult | null;
  stage_errors: Record<string, string | null> | null;
  timing: any;
  user_edits: Record<string, any>;
  status: 'pending' | 'enriching' | 'enriched' | 'reviewed' | 'exported' | 'failed';
  error_message: string | null;
  last_exported_at: string | null;
  export_count: number;
  created_at: string;
  updated_at: string;
}

export interface EnrichmentJobStatus {
  jobId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  mode?: 'sync' | 'async';
  amazonJobId?: string;
  progress: {
    total: number;
    completed: number;
    failed: number;
  };
}

export interface JobResultsResponse {
  jobId: string;
  status: string;
  results: EnrichmentResult[];
}

export interface HealthStatus {
  available: boolean;
  error?: string;
}

// ============================================
// Service
// ============================================

class AmazonExportService extends BaseService {
  private readonly baseEndpoint = '/amazon-export';

  /**
   * Get available Amazon product types
   */
  async getProductTypes(): Promise<AmazonProductType[]> {
    return this.makeRequest<AmazonProductType[]>(`${this.baseEndpoint}/product-types`);
  }

  /**
   * Check if the Amazon service is available
   */
  async checkHealth(): Promise<HealthStatus> {
    return this.makeRequest<HealthStatus>(`${this.baseEndpoint}/health`);
  }

  /**
   * Start enrichment for selected products
   * @param productIds - IDs of approved upload products
   * @param amazonProductTypes - Optional map of productId → amazon product type
   */
  async enrichProducts(
    productIds: string[],
    amazonProductTypes?: Record<string, string>
  ): Promise<EnrichmentJobStatus> {
    return this.makeRequest<EnrichmentJobStatus>(`${this.baseEndpoint}/enrich`, {
      method: 'POST',
      body: JSON.stringify({ productIds, amazonProductTypes }),
    });
  }

  /**
   * Poll job status
   */
  async getJobStatus(jobId: string): Promise<EnrichmentJobStatus> {
    return this.makeRequest<EnrichmentJobStatus>(`${this.baseEndpoint}/jobs/${jobId}/status`);
  }

  /**
   * Fetch job results (persists to DB and returns)
   */
  async getJobResults(jobId: string): Promise<JobResultsResponse> {
    return this.makeRequest<JobResultsResponse>(`${this.baseEndpoint}/jobs/${jobId}/results`);
  }

  /**
   * Get stored enrichment for a specific product
   */
  async getEnrichment(productId: string): Promise<EnrichmentResult> {
    return this.makeRequest<EnrichmentResult>(
      `${this.baseEndpoint}/products/${productId}/enrichment`
    );
  }

  /**
   * Save user field edits for a product
   */
  async saveEdits(productId: string, edits: Record<string, any>): Promise<EnrichmentResult> {
    return this.makeRequest<EnrichmentResult>(
      `${this.baseEndpoint}/products/${productId}/enrichment`,
      {
        method: 'PUT',
        body: JSON.stringify({ edits }),
      }
    );
  }

  /**
   * Re-validate a product after edits
   */
  async validateProduct(productId: string): Promise<ValidationResult> {
    return this.makeRequest<ValidationResult>(
      `${this.baseEndpoint}/products/${productId}/validate`,
      { method: 'POST' }
    );
  }

  /**
   * Export products to xlsx — triggers file download
   */
  async exportToXlsx(productIds: string[]): Promise<void> {
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    const token = localStorage.getItem('access_token');

    const response = await fetch(`${API_BASE_URL}/api${this.baseEndpoint}/export`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ productIds }),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || `Export failed with status ${response.status}`);
    }

    // Trigger download
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `amazon-listing-${Date.now()}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}

export const amazonExportService = new AmazonExportService();
